import type {
  ClientMessage,
  IceCandidateInit,
  ProtocolErrorPayload,
  ServerMessage,
} from '@flowshare/protocol';
import {
  PROTOCOL_VERSION,
  createClientMessage,
  safeJsonParse,
  serializeMessage,
  validateServerMessage,
} from '@flowshare/protocol';

export type SignalingStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface SignalingClientOptions {
  roomCode: string;
  buildHandshake: () => ClientMessage;
  onMessage: (message: ServerMessage) => void;
  onStatus: (status: SignalingStatus) => void;
  onFatal: (error: ProtocolErrorPayload) => void;
  onError: (error: ProtocolErrorPayload) => void;
  onLatency: (milliseconds: number | null) => void;
  createWebSocket?: (url: string) => WebSocket;
  url?: string;
}

const FATAL_ERROR_CODES = new Set([
  'ROOM_CLOSED',
  'ROOM_EXPIRED',
  'ROOM_NOT_FOUND',
  'ROOM_FULL',
  'UNAUTHORIZED',
  'INVALID_ROOM_CODE',
  'ORIGIN_NOT_ALLOWED',
  'UNSUPPORTED_VERSION',
  'ALREADY_IN_ROOM',
  'PEER_REMOVED',
]);

const FATAL_CLOSE_CODES = new Set([4001, 4002, 4003, 4004, 4008]);
const MAX_RECONNECT_ATTEMPTS = 8;
const HEARTBEAT_INTERVAL_MS = 15_000;
const LIVENESS_TIMEOUT_MS = 45_000;

function defaultUrl(code: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws?code=${encodeURIComponent(code)}`;
}

/**
 * WebSocket signaling transport with automatic reconnection, heartbeats and
 * latency measurement. It validates every server frame against the shared Zod
 * schemas before handing it to the application.
 */
export class SignalingClient {
  private socket: WebSocket | null = null;
  private status: SignalingStatus = 'idle';
  private disposed = false;
  private joined = false;
  private fatal = false;
  private attempt = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastReceivedAt = 0;
  private heartbeatCounter = 0;
  private readonly pendingHeartbeats = new Map<string, number>();

  private readonly handleOffline = (): void => {
    if (this.disposed || this.fatal) return;
    // Force a clean reconnect instead of waiting for the heartbeat watchdog.
    if (this.socket) {
      try {
        this.socket.close(4000, 'offline');
      } catch {
        // Ignore.
      }
    }
  };

  private readonly handleOnline = (): void => {
    if (this.disposed || this.fatal || this.joined) return;
    this.attempt = 0;
    this.clearReconnectTimer();
    if (
      !this.socket ||
      this.socket.readyState === WebSocket.CLOSED ||
      this.socket.readyState === WebSocket.CLOSING
    ) {
      this.connect();
    }
  };

  constructor(private readonly options: SignalingClientOptions) {
    if (typeof window !== 'undefined') {
      window.addEventListener('offline', this.handleOffline);
      window.addEventListener('online', this.handleOnline);
    }
  }

  connect(): void {
    if (this.disposed) return;
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.CONNECTING ||
        this.socket.readyState === WebSocket.OPEN)
    ) {
      return;
    }
    this.clearReconnectTimer();

    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let socket: WebSocket;
    try {
      const url = this.options.url ?? defaultUrl(this.options.roomCode);
      socket = this.options.createWebSocket?.(url) ?? new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.socket = socket;

    this.connectTimeout = setTimeout(() => {
      if (this.socket === socket && socket.readyState === WebSocket.CONNECTING) {
        try {
          socket.close();
        } catch {
          // Ignore.
        }
      }
    }, 10_000);

    socket.onopen = () => {
      this.clearConnectTimeout();
      this.lastReceivedAt = Date.now();
      this.setStatus('reconnecting');

      try {
        socket.send(serializeMessage(this.options.buildHandshake()));
      } catch {
        socket.close();
        return;
      }

      this.startTimers();
    };

    socket.onmessage = (event: MessageEvent) => this.handleRawMessage(event.data);

    socket.onerror = () => {
      // onclose always follows; reconnection is handled there.
    };

    socket.onclose = (event: CloseEvent) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.joined = false;
      this.stopTimers();
      this.clearConnectTimeout();

      if (this.disposed || this.fatal) {
        this.setStatus('closed');
        return;
      }

      if (FATAL_CLOSE_CODES.has(event.code)) {
        this.fatal = true;
        this.setStatus('closed');
        return;
      }

      this.scheduleReconnect();
    };
  }

  private handleRawMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    this.lastReceivedAt = Date.now();

    const json = safeJsonParse(raw);
    if (!json.ok) return;

    const parsed = validateServerMessage(json.value);
    if (!parsed.ok) {
      console.warn('Ignoring invalid signaling frame', parsed.error.code);
      return;
    }

    const message = parsed.value;

    if (message.type === 'heartbeat_ack') {
      const sentAt = message.requestId
        ? this.pendingHeartbeats.get(message.requestId)
        : undefined;
      if (sentAt !== undefined) {
        this.pendingHeartbeats.delete(message.requestId ?? '');
        this.options.onLatency(Date.now() - sentAt);
      }
    }

    if (message.type === 'room_error') {
      if (message.payload.fatal || FATAL_ERROR_CODES.has(message.payload.code)) {
        this.fatal = true;
        this.setStatus('closed');
        this.options.onFatal(message.payload);
        try {
          this.socket?.close();
        } catch {
          // Ignore.
        }
        return;
      }
      this.options.onError(message.payload);
      return;
    }

    if (message.type === 'room_created' || message.type === 'room_joined') {
      this.joined = true;
      this.attempt = 0;
      this.setStatus('open');
    }

    this.options.onMessage(message);
  }

  send(message: ClientMessage): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN || !this.joined) {
      // Signals are regenerated when the mesh is rebuilt after a reconnect.
      return;
    }
    try {
      this.socket.send(serializeMessage(message));
    } catch {
      // A close event will trigger reconnection.
    }
  }

  sendOffer(targetPeerId: string, sdp: { type: 'offer'; sdp: string }, requestId?: string): void {
    this.send(createClientMessage('offer', { targetPeerId, sdp }, requestId));
  }

  sendAnswer(targetPeerId: string, sdp: { type: 'answer'; sdp: string }, requestId?: string): void {
    this.send(createClientMessage('answer', { targetPeerId, sdp }, requestId));
  }

  sendIceCandidate(
    targetPeerId: string,
    candidate: IceCandidateInit | null,
    requestId?: string,
  ): void {
    this.send(createClientMessage('ice_candidate', { targetPeerId, candidate }, requestId));
  }

  leave(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(serializeMessage(createClientMessage('leave_room', {})));
      } catch {
        // Ignore.
      }
    }
    this.dispose();
  }

  dispose(): void {
    this.disposed = true;
    if (typeof window !== 'undefined') {
      window.removeEventListener('offline', this.handleOffline);
      window.removeEventListener('online', this.handleOnline);
    }
    this.clearReconnectTimer();
    this.stopTimers();
    this.clearConnectTimeout();
    this.pendingHeartbeats.clear();

    const socket = this.socket;
    this.socket = null;
    if (socket) {
      try {
        socket.close(1000, 'leaving');
      } catch {
        // Ignore.
      }
    }

    this.setStatus('closed');
  }

  get isOpen(): boolean {
    return this.status === 'open' && this.socket?.readyState === WebSocket.OPEN;
  }

  private startTimers(): void {
    this.stopTimers();
    this.lastReceivedAt = Date.now();

    this.heartbeatTimer = setInterval(() => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
      this.heartbeatCounter += 1;
      const requestId = `hb-${this.heartbeatCounter}-${Date.now().toString(36)}`;
      const sentAt = Date.now();
      this.pendingHeartbeats.set(requestId, sentAt);
      if (this.pendingHeartbeats.size > 8) {
        const oldest = this.pendingHeartbeats.keys().next().value;
        if (oldest) this.pendingHeartbeats.delete(oldest);
      }
      try {
        this.socket.send(
          serializeMessage(createClientMessage('heartbeat', {}, requestId)),
        );
      } catch {
        // onclose handles reconnection.
      }
    }, HEARTBEAT_INTERVAL_MS);

    this.watchdogTimer = setInterval(() => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
      if (Date.now() - this.lastReceivedAt > LIVENESS_TIMEOUT_MS) {
        this.options.onLatency(null);
        try {
          this.socket.close(4000, 'heartbeat timeout');
        } catch {
          // Ignore.
        }
      }
    }, 5_000);
  }

  private stopTimers(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.watchdogTimer) clearInterval(this.watchdogTimer);
    this.heartbeatTimer = null;
    this.watchdogTimer = null;
  }

  private scheduleReconnect(): void {
    if (this.disposed || this.fatal) return;

    // While the browser is offline there is no point burning retry attempts.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.setStatus('reconnecting');
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connect();
      }, 3_000);
      return;
    }

    if (this.attempt >= MAX_RECONNECT_ATTEMPTS) {
      this.fatal = true;
      this.setStatus('closed');
      this.options.onFatal({
        code: 'SIGNALING_FAILED',
        message: 'The connection to the signaling service was lost.',
      });
      return;
    }

    this.attempt += 1;
    const base = Math.min(8_000, 500 * 2 ** (this.attempt - 1));
    const jitter = base * 0.2 * Math.random();
    this.setStatus('reconnecting');

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, base + jitter);
  }

  private setStatus(status: SignalingStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.options.onStatus(status);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private clearConnectTimeout(): void {
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
  }

  get protocolVersion(): number {
    return PROTOCOL_VERSION;
  }
}
